import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Divider,
  AppBar,
  Toolbar,
  Link,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  DirectionsCar as CarIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

export const PrivacyPage: React.FC = () => {
  const navigate = useNavigate();

  React.useEffect(() => {
    document.title = "Privacy Policy - adiLessons";
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Review the privacy policy for adiLessons. Learn how we handle student scheduling and automated WhatsApp reminder data.');
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#f4f6f9' }}>
      {/* Navigation Header */}
      <AppBar
        position="sticky"
        sx={{
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.05)',
          color: '#1a237e',
        }}
      >
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', px: 2 }}>
          <Box display="flex" alignItems="center" gap={1} onClick={() => navigate('/')} sx={{ cursor: 'pointer' }}>
            <CarIcon sx={{ color: '#1967D2', fontSize: 28 }} />
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 'bold', 
                color: '#2b3154', 
                letterSpacing: '-0.5px',
                fontFamily: '"Outfit", "Inter", sans-serif'
              }}
            >
              adi<span style={{ color: '#E53935', fontWeight: '900' }}>L</span>essons
            </Typography>
          </Box>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/')}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
          >
            Back to Home
          </Button>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ py: 6, flexGrow: 1 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 6 },
            borderRadius: 4,
            border: '1px solid #e0e0e0',
            boxShadow: '0 4px 24px rgba(0, 0, 0, 0.02)',
          }}
        >
          <Box display="flex" alignItems="center" gap={2} sx={{ mb: 3 }}>
            <SecurityIcon color="primary" sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="h4" fontWeight="bold" color="primary">
                Privacy Policy
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Last Updated: June 14, 2026
              </Typography>
            </Box>
          </Box>
          
          <Divider sx={{ my: 3 }} />

          <Typography variant="body1" color="text.secondary" paragraph sx={{ lineHeight: 1.7 }}>
            At <strong>adiLessons</strong> (operated under the brand name <strong>adiLessons</strong>), we are committed to protecting your privacy. This Privacy Policy serves as our UK GDPR compliant privacy notice, explaining how we collect, use, process, and protect personal data.
          </Typography>

          <Typography variant="h6" fontWeight="bold" sx={{ mt: 4, mb: 2, color: '#1a237e' }}>
            1. Data Controller & Contact Information
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph sx={{ lineHeight: 1.7 }}>
            adiLessons operates as the Data Controller for the personal data of our registered instructors, driving schools, and website visitors. For any privacy inquiries, Subject Access Requests (SARs), or data removal requests, please contact our DPO at:
            <br />• <strong>Email:</strong> support@adilesson.co.uk
            <br />• <strong>Jurisdiction:</strong> England and Wales, United Kingdom.
          </Typography>

          <Typography variant="h6" fontWeight="bold" sx={{ mt: 4, mb: 2, color: '#1a237e' }}>
            2. Controller vs. Processor Roles
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph sx={{ lineHeight: 1.7 }}>
            • <strong>adiLessons as Controller:</strong> We are the Controller for account details, billing logs, and preferences of our registered instructors and driving schools.
            <br />• <strong>adiLessons as Processor:</strong> When instructors or driving schools input student data, lesson histories, and grading sheets into the platform, the instructor/driving school acts as the <strong>Data Controller</strong>, and adiLessons acts as the <strong>Data Processor</strong>. Instructors are solely responsible for obtaining the necessary legal consent from their pupils under UK GDPR before uploading their information or linking automated notifications.
          </Typography>

          <Typography variant="h6" fontWeight="bold" sx={{ mt: 4, mb: 2, color: '#1a237e' }}>
            3. Personal Data Categories and Purposes
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 3, color: 'text.secondary', fontSize: '0.875rem', lineHeight: 1.7 }}>
            <li><strong>Account Identity:</strong> Names, email addresses, password hashes, and user roles (Instructor, Student, Admin). processed under the lawful basis of <strong>Performance of a Contract</strong>.</li>
            <li><strong>Contact Info:</strong> Phone numbers of instructors and pupils (specifically required to deliver automated WhatsApp lesson confirmations and reminders). processed under <strong>Performance of a Contract</strong> and <strong>Legitimate Interests</strong> (to ensure smooth operations and reduce scheduling conflicts).</li>
            <li><strong>Syllabus Progress:</strong> Records of pupil grades and checklist completions against the 19 DVSA syllabus items. processed under <strong>Performance of a Contract</strong> on behalf of the instructor.</li>
            <li><strong>Financial Metadata:</strong> Subscription status and transaction tokens. Raw card details never touch our databases; they are handled securely by PCI-DSS gateways (PayPal & Stripe). processed under <strong>Performance of a Contract</strong> and <strong>Legal Obligation</strong> (tax records).</li>
          </Box>

          <Typography variant="h6" fontWeight="bold" sx={{ mt: 4, mb: 2, color: '#1a237e' }}>
            4. Data Retention Periods
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph sx={{ lineHeight: 1.7 }}>
            We store personal data only as long as necessary to fulfill the purposes outlined. Upon instructor account deletion, all associated pupil directories, scheduling tables, and contact profiles are marked for immediate purging and permanently deleted from our active servers and backup layers within 30 days. Financial transaction logs are retained for up to 6 years as required under UK corporate and tax regulations.
          </Typography>

          <Typography variant="h6" fontWeight="bold" sx={{ mt: 4, mb: 2, color: '#1a237e' }}>
            5. Subprocessors & Data Residency
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph sx={{ lineHeight: 1.7 }}>
            All application data is hosted on secure Amazon Web Services (AWS) datacenters in London, United Kingdom. We do not transfer personal data outside the UK or the European Economic Area (EEA). We use the following subprocessors:
            <br />• <strong>AWS</strong>: Cloud server hosting and backup infrastructure.
            <br />• <strong>WhapiCell</strong>: API service provider utilized to deliver automatic WhatsApp messages from your paired phone number.
            <br />• <strong>Stripe / PayPal</strong>: Payment gateways processing subscription transactions.
          </Typography>

          <Typography variant="h6" fontWeight="bold" sx={{ mt: 4, mb: 2, color: '#1a237e' }}>
            6. Your Rights under UK GDPR
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph sx={{ lineHeight: 1.7 }}>
            Under UK data protection law, you possess key rights regarding your data:
            <br />• <strong>Right of Access:</strong> Request a copy of all personal data we hold about you.
            <br />• <strong>Right to Rectification:</strong> Ask us to correct inaccurate information.
            <br />• <strong>Right to Erasure:</strong> Request that we delete your personal data under certain conditions.
            <br />• <strong>Right to Restrict Processing & Object:</strong> Challenge how we process your information.
            <br />To exercise these rights, please email us at <Link href="mailto:support@adilesson.co.uk" sx={{ fontWeight: 'bold', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>support@adilesson.co.uk</Link>.
          </Typography>

          <Typography variant="h6" fontWeight="bold" sx={{ mt: 4, mb: 2, color: '#1a237e' }}>
            7. Lodging a Complaint
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            If you are unsatisfied with how we handle your personal data, you have the right to lodge a complaint with the UK supervisory authority: the <strong>Information Commissioner's Office (ICO)</strong>. Inquiries can be directed via their portal: <Link href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 'bold', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>https://ico.org.uk</Link>.
          </Typography>
        </Paper>
      </Container>

      {/* Footer */}
      <Box sx={{ py: 3, bgcolor: '#0d132a', color: '#90a4ae', textAlign: 'center', borderTop: '1px solid #1c2a4a' }}>
        <Typography variant="caption">
          © {new Date().getFullYear()} adiLessons. All rights reserved. Designed in the UK.
        </Typography>
      </Box>
    </Box>
  );
};

export default PrivacyPage;
